import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from 'sonner';
import {
  AlertTriangle, Clock, CheckCircle2, RefreshCw, FileSpreadsheet, Package, Briefcase,
  Wrench, Loader2, Mail,
} from 'lucide-react';

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');

export default function FacturasPendientesRecepcion() {
  const { user } = useAuth();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canMark = ['admin', 'administrador', 'gerencia', 'contabilidad'].includes(role);

  const [data, setData] = useState({ en_plazo: [], atrasadas_5d: [], atrasadas_10d: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [grupo, setGrupo] = useState('atrasadas_10d'); // grupo activo
  const [busqueda, setBusqueda] = useState('');
  const [marcarDlg, setMarcarDlg] = useState(null);
  const [marcando, setMarcando] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/flujo-caja/cxp/facturas-pendientes-recepcion');
      setData(res.data || { en_plazo: [], atrasadas_5d: [], atrasadas_10d: [], total: 0 });
    } catch {
      toast.error('Error al cargar facturas pendientes de recepción');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grupos = {
    atrasadas_10d: { items: data.atrasadas_10d, label: 'Críticas (>10d)', color: 'red', icon: AlertTriangle },
    atrasadas_5d:  { items: data.atrasadas_5d,  label: 'Atrasadas (5-10d)', color: 'amber', icon: Clock },
    en_plazo:      { items: data.en_plazo,      label: 'En plazo (≤5d)', color: 'emerald', icon: CheckCircle2 },
  };
  const actual = grupos[grupo];
  const items = (actual.items || []).filter((f) => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return [f.folio, f.nombre_proveedor, f.rut_proveedor].some((v) => (v || '').toLowerCase().includes(q));
  });

  const totalMonto = (lista) => (lista || []).reduce((s, f) => s + (Number(f.monto) || 0), 0);

  const abrirMarcar = (f) => setMarcarDlg({
    ...f,
    tipo_factura: f.tipo_factura || 'material',
    es_anticipo: !!f.es_anticipo,
    requiere_recepcion: f.requiere_recepcion !== false,
    motivo_anticipo: f.motivo_anticipo || '',
    recepcion_limite_dias: 5,
  });

  const confirmarMarcar = async () => {
    setMarcando(true);
    try {
      await api.post(`/flujo-caja/cxp/factura/${marcarDlg.alegra_id}/marcar`, {
        tipo_factura: marcarDlg.tipo_factura,
        requiere_recepcion: marcarDlg.tipo_factura === 'servicio' ? false : marcarDlg.requiere_recepcion,
        es_anticipo: marcarDlg.es_anticipo,
        motivo_anticipo: marcarDlg.motivo_anticipo || '',
        recepcion_limite_dias: Number(marcarDlg.recepcion_limite_dias) || 5,
      });
      toast.success(`Factura ${marcarDlg.folio} marcada como ${marcarDlg.tipo_factura}`);
      setMarcarDlg(null);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al marcar factura');
    } finally { setMarcando(false); }
  };

  const exportarExcel = async () => {
    try {
      const all = [
        ...data.atrasadas_10d.map((f) => ({ ...f, _grupo: 'Críticas' })),
        ...data.atrasadas_5d.map((f) => ({ ...f, _grupo: 'Atrasadas' })),
        ...data.en_plazo.map((f) => ({ ...f, _grupo: 'En plazo' })),
      ];
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(all.map((f) => ({
        Grupo: f._grupo,
        Folio: f.folio,
        'RUT Proveedor': f.rut_proveedor,
        Proveedor: f.nombre_proveedor,
        Monto: f.monto,
        'Días s/recepción': f.dias_sin_recepcion,
        'Fecha emisión': f.fecha_emision,
        'Fecha contab. AR': f.fecha_contabilizacion_ar,
        Vencimiento: f.fecha_vencimiento,
        'Tipo factura': f.tipo_factura,
        Anticipo: f.es_anticipo ? 'Sí' : 'No',
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pendientes Recepción');
      XLSX.writeFile(wb, `facturas_pendientes_recepcion_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Facturas Pendientes de Recepción
          </h1>
          <p className="text-sm text-muted-foreground">
            Facturas contabilizadas en AR que aún no se han recepcionado en bodega. Total: <strong>{data.total}</strong> ·
            Monto comprometido: <strong>${fmtCLP(totalMonto([...data.atrasadas_10d, ...data.atrasadas_5d, ...data.en_plazo]))}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={exportarExcel}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* KPIs (3 grupos) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(['atrasadas_10d', 'atrasadas_5d', 'en_plazo']).map((key) => {
          const g = grupos[key];
          const Icon = g.icon;
          const active = grupo === key;
          const colorMap = {
            red: { bg: 'bg-red-50/70', border: 'border-red-300', ring: 'ring-red-400', text: 'text-red-700' },
            amber: { bg: 'bg-amber-50/70', border: 'border-amber-300', ring: 'ring-amber-400', text: 'text-amber-700' },
            emerald: { bg: 'bg-emerald-50/70', border: 'border-emerald-300', ring: 'ring-emerald-400', text: 'text-emerald-700' },
          };
          const c = colorMap[g.color];
          return (
            <button key={key} onClick={() => setGrupo(key)}
              className={`text-left p-3 rounded-lg border ${c.bg} transition-all ${active ? `ring-2 ${c.ring}` : `hover:${c.border}`}`}
              data-testid={`kpi-${key}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 ${c.text}`}>
                <Icon className="w-3 h-3" /> {g.label}
              </p>
              <p className={`text-2xl font-bold ${c.text}`}>{(g.items || []).length}</p>
              <p className={`text-[10px] ${c.text} opacity-80`}>${fmtCLP(Math.round(totalMonto(g.items)))}</p>
            </button>
          );
        })}
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-3 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Buscar en {actual.label}</Label>
            <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Folio, proveedor, RUT…" className="h-9 mt-1" data-testid="input-search" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4"><TableSkeleton rows={8} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {grupo === 'atrasadas_10d' && '🎉 No hay facturas críticas (>10d sin recepción)'}
              {grupo === 'atrasadas_5d' && 'No hay facturas atrasadas (5-10d)'}
              {grupo === 'en_plazo' && 'No hay facturas recientes en plazo'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs text-left">
                  <tr>
                    <th className="px-3 py-2">Folio</th>
                    <th className="px-3 py-2">Proveedor</th>
                    <th className="px-3 py-2 text-right">Monto</th>
                    <th className="px-3 py-2 text-center">Días</th>
                    <th className="px-3 py-2">Vencimiento</th>
                    <th className="px-3 py-2">Tipo</th>
                    {canMark && <th className="px-3 py-2 text-center">Acción</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => (
                    <tr key={f.alegra_id} className="border-t hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-mono text-xs">{f.folio}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium">{f.nombre_proveedor}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{f.rut_proveedor}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">${fmtCLP(Math.round(f.monto || 0))}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[11px] font-bold ${
                          f.dias_sin_recepcion > 10 ? 'text-red-700' :
                          f.dias_sin_recepcion > 5 ? 'text-amber-700' : 'text-emerald-700'
                        }`}>
                          {f.dias_sin_recepcion}d
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{f.fecha_vencimiento || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {f.tipo_factura === 'servicio' ? (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5">
                            <Briefcase className="w-3 h-3" /> Servicio
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                            <Package className="w-3 h-3" /> Material
                          </span>
                        )}
                        {f.es_anticipo && (
                          <span className="ml-1 inline-flex text-[10px] bg-orange-50 text-orange-700 border border-orange-200 rounded px-1.5 py-0.5">
                            Anticipo
                          </span>
                        )}
                      </td>
                      {canMark && (
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => abrirMarcar(f)}
                            className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                            title="Marcar tipo / anticipo"
                            data-testid={`btn-marcar-${f.alegra_id}`}>
                            <Wrench className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5" />
        Próximamente: recordatorios automáticos por email a compras/bodega/corporativo/gerencia configurables a los 5 y 10 días.
      </p>

      {/* Marcar dialog */}
      <Dialog open={!!marcarDlg} onOpenChange={(o) => { if (!o) setMarcarDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-marcar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Wrench className="w-5 h-5" /> Marcar Factura {marcarDlg?.folio}
            </DialogTitle>
            <DialogDescription>
              {marcarDlg?.nombre_proveedor} · ${fmtCLP(Math.round(marcarDlg?.monto || 0))}
            </DialogDescription>
          </DialogHeader>
          {marcarDlg && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-medium">Tipo de factura</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {(['material', 'servicio']).map((t) => (
                    <button key={t} type="button"
                      onClick={() => setMarcarDlg({ ...marcarDlg, tipo_factura: t })}
                      className={`p-2 rounded border text-xs transition-colors flex items-center justify-center gap-1 ${
                        marcarDlg.tipo_factura === t
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`btn-tipo-${t}`}>
                      {t === 'material' ? <Package className="w-3.5 h-3.5" /> : <Briefcase className="w-3.5 h-3.5" />}
                      {t === 'material' ? 'Material (requiere recepción)' : 'Servicio (sin recepción)'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="anticipo"
                  checked={marcarDlg.es_anticipo}
                  onChange={(e) => setMarcarDlg({ ...marcarDlg, es_anticipo: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="check-anticipo" />
                <Label htmlFor="anticipo" className="text-sm cursor-pointer">
                  Es un anticipo (contabilizada antes de la recepción definitiva)
                </Label>
              </div>
              {marcarDlg.es_anticipo && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 space-y-2">
                  <div>
                    <Label className="text-xs">Motivo del anticipo</Label>
                    <Input value={marcarDlg.motivo_anticipo}
                      onChange={(e) => setMarcarDlg({ ...marcarDlg, motivo_anticipo: e.target.value })}
                      placeholder="Ej: 50% adelanto, pago anticipado por descuento…"
                      className="h-8 text-xs mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Recordar recepción en (días)</Label>
                    <Input type="number" min="1" max="60"
                      value={marcarDlg.recepcion_limite_dias}
                      onChange={(e) => setMarcarDlg({ ...marcarDlg, recepcion_limite_dias: e.target.value })}
                      className="h-8 text-xs mt-1 w-24" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarcarDlg(null)} disabled={marcando}>Cancelar</Button>
            <Button onClick={confirmarMarcar} disabled={marcando} data-testid="btn-confirm-marcar">
              {marcando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
